"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

export function TestFundsBanner() {
  const { t } = useTranslation();
  const [state, setState] = useState<{
    cashCents: bigint | null;
    busy: boolean;
    error: string | null;
  }>({ cashCents: null, busy: false, error: null });

  useEffect(() => {
    let cancelled = false;
    void load();
    async function load() {
      if (!loadTokens()) return;
      try {
        const wallets = await api.listMyWallets();
        // Match the SiteHeader any-cash-wallet picker so the
        // "claim test funds" banner doesn't show for users with a
        // funded wallet of any currency. Previous filter explicitly
        // listed (USDC OR BRL) which would have stranded any user
        // with a USD wallet — unlikely in closed beta but the
        // any-currency filter is more robust as locales expand.
        const cash = wallets.find((w) => w.kind === "cash");
        if (cancelled) return;
        setState((s) => ({
          ...s,
          cashCents: cash ? BigInt(cash.balance_cents) : BigInt(0),
        }));
      } catch {
        // silent — banner just won't show
      }
    }
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleGrant() {
    if (!loadTokens()) return;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const res = await api.grantTestFunds();
      setState({
        cashCents: BigInt(res.grantedCents),
        busy: false,
        error: null,
      });
    } catch (err) {
      setState((s) => ({
        ...s,
        busy: false,
        error:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("testFundsBanner.errorFallback"),
      }));
    }
  }

  if (state.cashCents === null) return null;
  if (state.cashCents > BigInt(0)) return null;

  return (
    <div
      className="mb-4 flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center"
      style={{
        borderColor: "color-mix(in srgb, var(--color-brand) 30%, transparent)",
        background: "color-mix(in srgb, var(--color-brand) 5%, transparent)",
      }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-[var(--color-text)]">
          {t("testFundsBanner.title")}
        </p>
        <p className="mt-0.5 text-[11px] text-[var(--color-text2)]">
          {t("testFundsBanner.body")}
        </p>
        {state.error && (
          <p className="mt-1 text-[11px]" style={{ color: "var(--color-no)" }}>
            {state.error}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleGrant}
        disabled={state.busy}
        className="rounded-md px-4 py-2 text-[12px] font-semibold text-white transition-colors disabled:opacity-60"
        style={{ background: "var(--color-brand)" }}
      >
        {state.busy
          ? t("testFundsBanner.granting")
          : t("testFundsBanner.grantCta")}
      </button>
    </div>
  );
}
